import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import PromptTableItem from './PromptTableItem';
import {usePrompts} from "../hooks/usePrompts";
import Loader from "./Loader";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import AddPrompt from "./AddPrompt";

const PromptsTab = ({company, onPromptSelect}) => {
    const [currentTab, setCurrentTab] = useState(0);
    const {prompts, loading, error, fetchPrompts} = usePrompts();
    const [addPrompt, setAddPrompt] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        keyword: '',
        type: '',
        nonSystemOnly: false,
        format: null
    });

    const [availableTypes, setAvailableTypes] = useState([]);
    const [availableFormats, setAvailableFormats] = useState([]);
    const [searchTimeout, setSearchTimeout] = useState(null);

    // Map tab indices to category objects with display name and API value
    const tabCategories = [
        {display: "Articles", value: "ARTICLES"},
        {display: "Single Post", value: "SINGLE_POST"},
        {display: "Article Titles", value: "ARTICLE_TITLES"},
        {display: "Article Taglines", value: "ARTICLE_TAGLINES"},
        {display: "Banner Titles", value: "BANNER_TITLES"},
        {display: "News", value: "NEWS"},
        {display: "Weekly News", value: "WEEKLY_NEWS"}
    ];

    // Available prompt types (you can fetch this from an API or define statically)
    const promptTypes = [
        {display: "Format", value: "FORMAT"},
        {display: "Sub Format", value: "SUB_FORMAT"},
        {display: "Prompt", value: "PROMPT"},
    ];

    const category = tabCategories[currentTab].value;

    const handleClose = () => {
        setAddPrompt(false);
    };

    const handleTabChange = (event, val) => {
        setCurrentTab(val);
        // Reset filters when changing tabs
        setFilters(prev => ({
            ...prev,
            keyword: '',
            type: '',
            format: null // Also reset format when changing tabs
        }));
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const handleKeywordChange = (value) => {
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Update keyword immediately for UI responsiveness
        setFilters(prev => ({
            ...prev,
            keyword: value
        }));

        // Set new timeout for API call
        const timeout = setTimeout(() => {
            // The actual fetch will be triggered by useEffect
        }, 500); // 500ms debounce

        setSearchTimeout(timeout);
    };

    const clearFilters = () => {
        setFilters({
            keyword: '',
            type: '',
            nonSystemOnly: false,
            format: null
        });
    };

    const hasActiveFilters = filters.keyword || filters.type || filters.nonSystemOnly || filters.format;

    // Extract available formats from current prompts
    useEffect(() => {
        if (prompts && prompts.length > 0) {
            // Get unique formats from the current prompt results
            const formats = prompts
                .filter(prompt => prompt.formatId && prompt.formatName) // Only include prompts with valid format data
                .reduce((unique, prompt) => {
                    // Remove duplicates based on format id
                    if (!unique.find(f => f.formatId === prompt.formatId)) {
                        unique.push({
                            formatId: prompt.formatId,
                            formatName: prompt.formatName
                        });
                    }
                    return unique;
                }, [])
                .sort((a, b) => a.formatName.localeCompare(b.formatName)); // Sort alphabetically by name

            setAvailableFormats(formats);

            // If current selected format is not in the available formats, clear it
            if (filters.format && !formats.find(f => f.formatId === filters.format.formatId)) {
                setFilters(prev => ({
                    ...prev,
                    format: null
                }));
            }
        } else {
            // No prompts available, clear formats
            setAvailableFormats([]);
            if (filters.format) {
                setFilters(prev => ({
                    ...prev,
                    format: null
                }));
            }
        }
    }, [prompts, filters.format]);

    useEffect(() => {
        const filterParams = {
            companyId: company?.id,
            category: category,
            ...(filters.keyword && {keyword: filters.keyword}),
            ...(filters.type && {type: filters.type}),
            nonSystemOnly: company?.id != null,
            formatId: filters.format?.formatId || null
        };

        fetchPrompts(filterParams);
    }, [company?.id, category, filters, fetchPrompts]);

    // Load available types for the current category
    useEffect(() => {
        if (category) {
            // You might want to fetch available types from your backend
            setAvailableTypes(promptTypes);
        }
    }, [company?.id, category]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    return (
        <Box>
            <AddPrompt company={company} open={addPrompt} handleClose={handleClose}/>
            <Loader loading={loading}/>

            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography sx={{
                    fontWeight: 700,
                    fontSize: { md: "32px", xs: "24px" },
                    lineHeight: { md: "40px", xs: "32px" }
                }} color="primary.main">
                    Prompts
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddPrompt(true)}
                >
                    Add Prompt
                </Button>
            </Box>

            {/* Category Tabs */}
            <Paper sx={{ mb: 3 }} elevation={1}>
                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    aria-label="prompt categories"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {tabCategories.map((category, index) => (
                        <Tab
                            key={index}
                            sx={{textTransform: "none"}}
                            label={category.display}
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* Filters Section */}
            <Paper elevation={1} sx={{p: 2, mb: 3}}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem" }}>
                    Filters
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                    {/* Keyword Search */}
                    <TextField
                        size="small"
                        placeholder="Search prompts..."
                        value={filters.keyword}
                        onChange={(e) => handleKeywordChange(e.target.value)}
                        sx={{ minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Type Filter */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={filters.type}
                            label="Type"
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                        >
                            <MenuItem value="">
                                <em>All Types</em>
                            </MenuItem>
                            {availableTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.display}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Format Filter - Only show when type is SUB_FORMAT */}
                    {filters.type === "SUB_FORMAT" && (
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Format</InputLabel>
                            <Select
                                value={filters.format?.formatId || ''}
                                label="Format"
                                onChange={(e) => {
                                    const selectedFormat = e.target.value
                                        ? availableFormats.find(f => f.formatId === e.target.value)
                                        : null;
                                    handleFilterChange('format', selectedFormat);
                                }}
                                disabled={availableFormats.length === 0}
                            >
                                <MenuItem value="">
                                    <em>All Formats</em>
                                </MenuItem>
                                {availableFormats.map((format) => (
                                    <MenuItem key={format.formatId} value={format.formatId}>
                                        {format.formatName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <Tooltip title="Clear all filters">
                            <IconButton
                                onClick={clearFilters}
                                color="error"
                                size="small"
                            >
                                <FilterAltOffIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Results Count */}
                    <Typography variant="caption" color="text.secondary">
                        {prompts ? `${prompts.length} results` : ''}
                    </Typography>
                </Box>
            </Paper>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{mb: 2}}>
                    {error}
                </Alert>
            )}

            {/* Results Table */}
            <Paper sx={{ p: 2 }} elevation={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem" }}>
                        Prompt Results
                    </Typography>
                </Box>
                <Table sx={{
                    tableLayout: "fixed",
                    width: "100%",
                    "&.MuiTable-root": {
                        borderCollapse: "separate",
                        borderSpacing: "0 8px",
                    }
                }}>
                    <TableHead>
                        <TableRow sx={{ "& .MuiTableCell-root": { padding: "8px 12px", fontSize: "0.8rem", fontWeight: 600 } }}>
                            <TableCell align="left" sx={{ width: "30%" }}>Name</TableCell>
                            <TableCell align="center" sx={{ width: "18%" }}>Category</TableCell>
                            <TableCell align="center" sx={{ width: "15%" }}>Type</TableCell>
                            <TableCell align="center" sx={{ width: "22%" }}>Format Name</TableCell>
                            <TableCell align="center" sx={{ width: "15%" }}>Created</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prompts && prompts.length > 0 ? (
                            prompts.map((prompt, i) => (
                                <PromptTableItem
                                    item={prompt}
                                    key={i}
                                    onSelect={() => onPromptSelect(prompt)}
                                />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {hasActiveFilters
                                        ? "No prompts found matching the current filters"
                                        : "No prompts found for this category"
                                    }
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
};

export default PromptsTab;
